// OAuth 2.0 authorization policy for the /authorize endpoint (Claude MCP).
//
// This file is the single source of truth for WHO may connect, WHERE we are
// allowed to send the user afterwards, and WHAT the consent screen promises.
// No other module may contain a redirect URI, a role list, or consent copy.

export const CLAUDE_CLIENT_ID = "2687cb8b-2fa4-4c1b-92af-e5c464573746";

// Branding only. A well-formed but unrecognised client_id is NOT rejected --
// the backend is the authority on which clients are registered. We simply
// never CLAIM that an unknown client is Claude.
export const KNOWN_CLIENTS = {
  [CLAUDE_CLIENT_ID]: { name: "Claude", vendor: "Anthropic" },
};

// Exact-string redirect URI allowlist (RFC 6749 3.1.2.3 simple string
// comparison). No prefix matching, no host-suffix matching, no normalisation,
// no trailing-slash tolerance, no wildcards. Registering a new callback is a
// one-line edit HERE and nowhere else.
const PROD_REDIRECT_URIS = [
  "https://claude.ai/oauth/callback",
  "https://claude.com/oauth/callback",
  "https://claude.ai/api/mcp/auth_callback",
  "https://claude.com/api/mcp/auth_callback",
];

// Loopback callbacks for local development only. Gated on NODE_ENV (which CRA
// sets automatically) rather than an env var, so this can never be widened by
// a .env edit -- .env is tracked in git and CRA inlines REACT_APP_* at build
// time, so an allowlist flag there would ship straight to production.
const DEV_REDIRECT_URIS = [
  "http://localhost:3000/oauth/callback",
  "http://127.0.0.1:3000/oauth/callback",
];

export const ALLOWED_REDIRECT_URIS =
  process.env.NODE_ENV === "production"
    ? PROD_REDIRECT_URIS
    : [...PROD_REDIRECT_URIS, ...DEV_REDIRECT_URIS];

// Roles allowed to reach the consent screen with an ALREADY-ACTIVE session:
// the three the app treats as "a shipper" everywhere else
// (AuthenticationRoutes.jsx, ShipperRoutes.jsx, LoginForm.jsx), plus Admin.
//
// Admin is consent-only by design. It is NOT in OAUTH_INLINE_LOGIN_ROLES
// below, so an admin can approve a connection only if they were already
// signed in to the admin portal before arriving here. See that constant for
// why the two lists must stay different.
export const OAUTH_ELIGIBLE_ROLES = [
  "Shipper",
  "ShipperUser",
  "oneTimeShipper",
  "Admin",
];

// Roles that may be obtained by signing in ON the /authorize page itself.
//
// Admin is deliberately absent. /authorize is a public, unauthenticated URL
// reachable by anyone Claude redirects, and the admin portal deliberately
// lives behind its own separate address -- putting an admin sign-in path on
// this page would advertise and expose that entry point to every shipper and
// every stranger who lands here. An admin who is not already signed in has no
// route to an admin grant from this page at all; they must sign in to the
// admin portal first and come back.
//
// This list is checked against the role the BACKEND RETURNS after login, not
// the one the card submits, so it holds even if a login POST were ever made
// to resolve an admin account.
export const OAUTH_INLINE_LOGIN_ROLES = [
  "Shipper",
  "ShipperUser",
  "oneTimeShipper",
];

// Roles that see the admin-scoped consent copy instead of the shipper copy.
export const OAUTH_ADMIN_ROLES = ["Admin"];

export const isOauthAdminRole = (role) => OAUTH_ADMIN_ROLES.includes(role);

export const ROLE_LABELS = {
  Shipper: "Shipper",
  ShipperUser: "Shipper (team member)",
  oneTimeShipper: "Guest shipper",
  Carrier: "Carrier",
  warehouseUser: "3PL",
  Admin: "Administrator",
};

// Value POSTed to `login` from the OAuth sign-in card, and the ONLY value it
// ever posts. ShipperUser accounts authenticate through this same value
// (matches roleMap.shipper in LoginForm.jsx); the backend resolves the real
// role, which is re-checked against OAUTH_INLINE_LOGIN_ROLES after login.
export const OAUTH_LOGIN_ROLE = "Shipper";

export const DEFAULT_SCOPE = "openid profile email";

export const SCOPE_LABELS = {
  openid: "Confirm your Hajex Bolt identity",
  profile: "Your name and account details",
  email: "Your email address",
};

// The human-readable consent list. Deliberately NOT derived from the scope
// string: the OIDC scope is only `openid profile email`, but the grant the
// user is actually giving is read access to orders, shipments and products.
export const GRANTS = [
  {
    key: "orders",
    title: "View your orders",
    detail: "Order details, line items, status and history",
  },
  {
    key: "shipments",
    title: "View your shipments",
    detail: "Carriers, rates, labels and tracking status",
  },
  {
    key: "products",
    title: "View your products",
    detail: "Product catalogue, SKUs and package presets",
  },
  {
    key: "profile",
    title: "Confirm who you are",
    detail:
      "Your name and email address, so it knows whose account it is connected to",
  },
];

// Admin-scoped consent copy. Deliberately NOT a tweak of GRANTS: an Admin
// token is not scoped to one account, so every line that says "your" in the
// shipper copy would be an understatement of what is actually being granted.
export const ADMIN_GRANTS = [
  {
    key: "orders",
    title: "View orders across all accounts",
    detail: "Order details, line items, status and history, for any customer",
  },
  {
    key: "shipments",
    title: "View shipments across all accounts",
    detail: "Carriers, rates, labels and tracking status, for any customer",
  },
  {
    key: "products",
    title: "View products across all accounts",
    detail: "Product catalogues, SKUs and package presets, for any customer",
  },
  {
    key: "profile",
    title: "Confirm who you are",
    detail:
      "Your name and email address, so it knows whose account it is connected to",
  },
];

export const RESTRICTIONS = [
  { key: "write", title: "Create, edit, cancel or purchase shipments" },
  { key: "payment", title: "See your password, card or bank details" },
  { key: "settings", title: "Change any account or user setting" },
];

export const OAUTH_ERRORS = {
  INVALID_REQUEST: "invalid_request",
  UNSUPPORTED_RESPONSE_TYPE: "unsupported_response_type",
  INVALID_SCOPE: "invalid_scope",
  ACCESS_DENIED: "access_denied",
  SERVER_ERROR: "server_error",
  TEMPORARILY_UNAVAILABLE: "temporarily_unavailable",
};

// PKCE challenge length window. RFC 7636 4.2 specifies 43..128, but the sample
// URL this was built against carries the 28-character placeholder
// `base64url-sha256-of-verifier`; a strict floor of 43 would bounce that exact
// URL back to the client with invalid_request and the first end-to-end test
// would look like a bug here. A console.warn fires below 43 so a genuinely
// short challenge stays visible. Tighten to 43 once a real client request has
// been captured.
export const PKCE_MIN_LENGTH = 8;
export const PKCE_MAX_LENGTH = 128;
export const PKCE_RFC_MIN_LENGTH = 43;
